// ============================================================================
// TenantService - Hostname-Based Tenant Detection & Theming
// ============================================================================
// Detects tenant (Arbitech/Velasea) from window.location.hostname on startup.
// Replaces V1's companyInfo hostname switch in app.js. Sets CSS custom properties
// on :root for brand colors so all components theme automatically.
// ============================================================================

import { Injectable } from '@angular/core';

@Injectable()
export class TenantService {
  company = '';
  companyName = '';
  companyId = '';
  logoUrl = '';

  constructor() {
    // Hostname-based detection for fast initial paint — this runs synchronously
    // at app construction, before init() has a chance to ask the server.
    this.resolve(window.location.hostname);
    this.applyTheme();
    this.applyDocumentChrome();
  }

  /** Bootstrap-time fetch of the server's resolved tenant.
   *
   *  The server reads LOCAL_DEV_TENANT (env var) for localhost, which the
   *  browser can't see. Calling /api/tenant lets the client mirror exactly
   *  what the server scoped this request to — eliminates client/server drift
   *  when flipping the dev tenant. Wired via APP_INITIALIZER so the theme is
   *  authoritative before any component renders.
   *
   *  Falls back silently to the hostname-based detection from the constructor
   *  if the request fails (offline, server down during dev, etc.). */
  init(): Promise<void> {
    return fetch('/api/tenant', { credentials: 'same-origin' })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.company) {
          this.company = data.company;
          this.companyName = data.companyName || '';
          this.companyId = data.companyId || '';
          this.applyTheme();
          this.applyDocumentChrome();
        }
      })
      .catch(() => {/* keep constructor-resolved values */});
  }

  /** Sync the browser tab's title and favicon with the resolved tenant.
   *  index.html sets these once from hostname for fast initial paint, but
   *  this method makes TenantService the authoritative source so any
   *  future override (e.g. ?tenant= query param) stays in sync. */
  private applyDocumentChrome(): void {
    document.title = `${this.companyName} Customer Portal`;
    const favicon = document.getElementById('dynamic-favicon') as HTMLLinkElement | null;
    if (favicon) {
      favicon.href = `/images/branding/${this.company}/favicon.png`;
    }
  }

  private resolve(hostname: string): void {
    switch (hostname.toLowerCase()) {
      //case "localhost":
      case 'my.arbitech.com':
      case 'arbitech.custportal.com':
      case 'custportal.arbitech.com':
        this.company = 'arb';
        this.companyName = 'Arbitech';
        this.companyId = 'ARB30131';
        break;
      case "localhost":
      case "velasea.custportal.com":
      case "custportal.velasea.com":
        this.company = 'vel';
        this.companyName = 'Velasea';
        this.companyId = 'VEL29641';
        break;
      default: // localhost and velasea variants
        this.company = "generic";
        this.companyName = "Generic Test Company";
        this.companyId = "generic";
        break;
    }
  }

  private applyTheme(): void {
    const root = document.documentElement;

    if (this.company === 'arb') {
      // Arbitech - green theme (rgb(0, 204, 127) primary)
      root.style.setProperty('--brand-primary', '#00cc7f');
      root.style.setProperty('--brand-primary-hover', '#00b370');
      root.style.setProperty('--brand-primary-gradient', 'linear-gradient(to bottom, #00cc7f, #00b370)');
      root.style.setProperty('--brand-btn-primary', '#00cc7f');
      root.style.setProperty('--brand-btn-primary-hover', '#00b370');
      root.style.setProperty('--brand-login-btn', '#00cc7f');
      root.style.setProperty('--brand-login-btn-hover', '#00b370');
      root.style.setProperty('--nav-text', '#CDCDCD');
      root.style.setProperty('--nav-text-hover', '#00cc7f');
      this.applyPrimeNgPrimary(root, '#00cc7f', '#00b370', '#33d695');
    } else {
      // Velasea - red theme (default)
      root.style.setProperty('--brand-primary', '#dd1c24');
      root.style.setProperty('--brand-primary-hover', '#cc242b');
      root.style.setProperty('--brand-primary-gradient', 'linear-gradient(to bottom, #dd1c24, #cc242b)');
      root.style.setProperty('--brand-btn-primary', '#b10000');
      root.style.setProperty('--brand-btn-primary-hover', '#d81515');
      root.style.setProperty('--brand-login-btn', '#b10000');
      root.style.setProperty('--brand-login-btn-hover', '#d81515');
      root.style.setProperty('--nav-text', '#fff');
      root.style.setProperty('--nav-text-hover', '#dd1c24');
      this.applyPrimeNgPrimary(root, '#dd1c24', '#cc242b', '#e8525a');
    }
  }

  /**
   * Override PrimeNG (Lara preset) primary-color tokens so its components —
   * input focus rings, p-button defaults, p-select highlights, table sort
   * indicators, etc. — pick up the tenant brand color instead of emerald.
   * Lara consumes these via --p-primary-color and --p-focus-ring-color.
   */
  private applyPrimeNgPrimary(root: HTMLElement, base: string, dark: string, light: string): void {
    root.style.setProperty('--p-primary-color', base);
    root.style.setProperty('--p-primary-contrast-color', '#ffffff');
    root.style.setProperty('--p-primary-hover-color', dark);
    root.style.setProperty('--p-primary-active-color', dark);
    root.style.setProperty('--p-focus-ring-color', base);
    root.style.setProperty('--p-form-field-focus-border-color', base);
    // Common Lara primary-scale tokens — the 500 mid-tone is what most
    // components reference. 600 is hover, 400 is the lighter accent.
    root.style.setProperty('--p-primary-400', light);
    root.style.setProperty('--p-primary-500', base);
    root.style.setProperty('--p-primary-600', dark);
    // List-option highlight tokens (p-select dropdown, autocomplete, listbox).
    // Default Lara emits emerald greens here even after primary is overridden,
    // so we explicitly point them at the brand color: a soft tint background
    // with brand-tone text.
    const tint = `color-mix(in srgb, ${base} 12%, transparent)`;
    const tintHover = `color-mix(in srgb, ${base} 18%, transparent)`;
    root.style.setProperty('--p-highlight-background', tint);
    root.style.setProperty('--p-highlight-color', dark);
    root.style.setProperty('--p-list-option-selected-background', tint);
    root.style.setProperty('--p-list-option-selected-color', dark);
    root.style.setProperty('--p-list-option-selected-focus-background', tintHover);
    root.style.setProperty('--p-list-option-selected-focus-color', dark);
    root.style.setProperty('--p-list-option-focus-background', tintHover);
    root.style.setProperty('--p-list-option-focus-color', dark);
    // p-select trigger when focused/open
    root.style.setProperty('--p-select-focus-border-color', base);
  }
}
